import { useCallback, type RefObject } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { useSelectedGcsConfigsStore } from '../store/useSelectedGcsConfigsStore';
import { useSelectedVehicleStore } from '../store/useSelectedVehicleStore';
import { useGcsStore } from '../store/useGcsStore';
import { useMissionVisibilityStore } from '../store/useMissionVisibilityStore';

interface MapPadding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Calculates dynamic map padding, accounting for side panels and vehicle panel.
 * Reads from CSS variables: --panel-gap, --side-panel-width, --vehicle-panel-width
 *
 * @param isVehiclePanelOpen - Whether the vehicle panel is currently open
 * @returns Padding object with top, bottom, left, and right values in pixels
 */
export function calculateMapPadding(isVehiclePanelOpen: boolean): MapPadding {
  const root = document.documentElement;
  const styles = getComputedStyle(root);

  const panelGap = parseFloat(styles.getPropertyValue('--panel-gap')) || 16;
  const sidePanelWidth = parseFloat(styles.getPropertyValue('--side-panel-width')) || 400;
  const vehiclePanelWidth = parseFloat(styles.getPropertyValue('--vehicle-panel-width')) || 360;
  const standardPadding = 144;

  // Base left padding: gap + side panel + gap + extra padding
  let leftPadding = panelGap + sidePanelWidth + panelGap + standardPadding;

  // Add vehicle panel width if it's open
  if (isVehiclePanelOpen) {
    leftPadding += vehiclePanelWidth + panelGap;
  }

  return {
    top: standardPadding,
    bottom: standardPadding,
    left: leftPadding,
    right: standardPadding
  };
}

/**
 * Focuses the map on all relevant operational entities:
 * control zone, launchpads, live mission pickup/delivery points,
 * mission routes, failure-mode routes/safe points, drones and foreign aircraft.
 */
export const useFocusMap = (mapRef: RefObject<MapRef | null>) => {
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);
  const missionVisibility = useMissionVisibilityStore((state) => state.missionVisibility);

  return useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    const selectedGcsConfigs = useSelectedGcsConfigsStore.getState().selectedConfigs;
    const gcsDataMap = useGcsStore.getState().data;

    const coordinates: Array<[number, number]> = [];

    selectedGcsConfigs.forEach((config) => {
      const gcsData = gcsDataMap.get(config.id);
      if (!gcsData) return;

      const visibleMissionIds = new Set(
        Array.from(gcsData.liveMissionsMap.values())
          .filter(
            (mission) =>
              missionVisibility === 'all' ||
              (selectedVehicle?.gcsId === config.id && mission.vehicleId === selectedVehicle.vehicleId)
          )
          .map((mission) => mission.missionId)
      );

      gcsData.flightNetwork.data?.centerControlZone?.points.forEach((point) => {
        coordinates.push([point.lng, point.lat]);
      });

      gcsData.launchpads.data?.forEach((launchpad) => {
        coordinates.push([launchpad.location.lng, launchpad.location.lat]);
      });

      gcsData.liveMissionsMap.forEach((mission) => {
        const isMissionPointVisible = visibleMissionIds.has(mission.missionId);

        if (!isMissionPointVisible) return;

        if (mission.pickupPoint) {
          coordinates.push([mission.pickupPoint.location.lng, mission.pickupPoint.location.lat]);
        }
        coordinates.push([
          mission.package.order.deliveryPoint.location.lng,
          mission.package.order.deliveryPoint.location.lat
        ]);
      });

      gcsData.missionRouteMap.forEach((routeInfo, missionId) => {
        if (!visibleMissionIds.has(missionId)) return;

        routeInfo.route.forEach((node) => {
          coordinates.push([node.location.lng, node.location.lat]);
        });
      });

      gcsData.missionFailureModeRouteInfoMap.forEach((routeInfo, missionId) => {
        if (!visibleMissionIds.has(missionId)) return;

        routeInfo.routeToSafePoint.forEach((point) => {
          coordinates.push([point.lng, point.lat]);
        });
        routeInfo.safePoints.forEach((safePoint) => {
          coordinates.push([safePoint.location.lng, safePoint.location.lat]);
        });
      });

      gcsData.missionFlightDataMap.forEach((flightData) => {
        coordinates.push([flightData.location.lng, flightData.location.lat]);
      });

      gcsData.foreignAircraftDetailsMap.forEach((aircraftDetails) => {
        coordinates.push([aircraftDetails.location.lng, aircraftDetails.location.lat]);
      });
    });

    if (coordinates.length === 0) {
      return;
    }

    const longitudes = coordinates.map(([lng]) => lng);
    const latitudes = coordinates.map(([, lat]) => lat);

    let minLng = Math.min(...longitudes);
    let maxLng = Math.max(...longitudes);
    let minLat = Math.min(...latitudes);
    let maxLat = Math.max(...latitudes);

    const epsilon = 0.0005;
    if (minLng === maxLng) {
      minLng -= epsilon;
      maxLng += epsilon;
    }
    if (minLat === maxLat) {
      minLat -= epsilon;
      maxLat += epsilon;
    }

    mapRef.current.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat]
      ],
      {
        padding: calculateMapPadding(!!selectedVehicle),
        duration: 350,
        essential: true
      }
    );
  }, [mapRef, missionVisibility, selectedVehicle]);
};
